import { useState, useRef, useContext, useEffect } from "react";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import Form from "react-bootstrap/Form";
import { majorScales, minorScales } from "../assets/scales";
import { hymnCategories } from "../assets/hymnCategories";
import validate from "validate.js";
import { ToastService } from "./Toast";
import {
  collection,
  updateDoc,
  deleteDoc,
  getDocs,
  doc,
  where,
  query,
} from "firebase/firestore";
import { db, storage } from "../firebase/firebase";
import { ref, deleteObject, uploadBytes } from "firebase/storage";
import { useNavigate } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import { ChordsContext } from "../components/ChordsContext";
import Switch from "@mui/material/Switch";
import { transliterateGreekToEnglish } from "../components/Helpers";
import { PDFDocument, rgb } from "pdf-lib";
import { cleanHtmlFile, convertDocxToHtml, buildHymnHtml, parseHymnHtml } from "./hymnHtml";
import HymnEditor from "./HymnEditor";

export const EditHymnData = ({
  hymnTitle,
  hymnScale,
  isPublic,
  hymnCategory,
}) => {
  const [editData, setEditData] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedNotesFile, setSelectedNotesFile] = useState(null);
  const [selectedTitle, setSelectedTitle] = useState(hymnTitle);
  const [selectedScale, setSelectedScale] = useState("");
  const [selectedCategory, setSelectedCategory] = useState(hymnCategory);
  const [selectedNumber, setSelectedNumber] = useState("");
  const [showToastService, setShowToastService] = useState(false);
  const [toastMessage, setToastMessage] = useState(null);
  const [selectedImage, setSelectedImage] = useState("");
  const fileInputRef = useRef(null);
  const fileNotesInputRef = useRef(null);
  const navigate = useNavigate();
  const { editDetailsMode, setEditDetailsMode } = useContext(ChordsContext);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [isHymnPublic, setIsHymnPublic] = useState(false);
  const [stripPdfHeaderFooter, setStripPdfHeaderFooter] = useState(true);
  const [cleanHtml, setCleanHtml] = useState(true);
  const [previewBodyHtml, setPreviewBodyHtml] = useState("");

  useEffect(() => {
    setEditData(editDetailsMode);
    setIsHymnPublic(isPublic);
    // eslint-disable-next-line
  }, [editDetailsMode]);

  useEffect(() => {
    setSelectedScale(hymnScale);
    let selected = majorScales
      .concat(minorScales)
      .find((option) => option.value === hymnScale);
    if (selected) setSelectedImage(selected.image);
    // eslint-disable-next-line
  }, [hymnScale]);

  const showToastMessage = (message) => {
    setToastMessage(message);
    setShowToastService(!showToastService);
    setTimeout(() => {
      setShowToastService(false);
    }, 1500);
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (
      !file ||
      !(
        file.type === "text/html" ||
        file.type ===
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
        file.name.endsWith(".docx") ||
        file.name.endsWith(".html")
      )
    ) {
      showToastMessage("Παρακαλώ επιλέξτε αρχείο σε μορφή html ή docx.");
      return;
    }
    setSelectedFile(file);
    try {
      const isDocx =
        file.name.endsWith(".docx") ||
        file.type ===
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
      const htmlFile = isDocx ? await convertDocxToHtml(file) : file;
      const cleaned = cleanHtml ? await cleanHtmlFile(htmlFile) : htmlFile;
      const fullHtml = await cleaned.text();
      setPreviewBodyHtml(parseHymnHtml(fullHtml));
    } catch (err) {
      console.error("EditHymnData preview parse error", err);
    }
  };

  const handleNotesFileChange = (e) => {
    const file = e.target.files[0];
    if (file && file.type === "application/pdf") {
      setSelectedNotesFile(e.target.files[0]);
    } else {
      showToastMessage("Παρακαλώ επιλέξτε αρχείο σε μορφή pdf.");
    }
  };

  const handleCloseEditData = () => {
    setEditData(false);
    setEditDetailsMode(false);
  };

  const handleShowDeleteConfirmation = () => setShowDeleteConfirmation(true);
  const handleCloseDeleteConfirmation = () => setShowDeleteConfirmation(false);

  const handleDeleteHymn = async () => {
    // Check if title already exists
    const existingHymnSnapshot = await getDocs(
      query(collection(db, "hymns"), where("title", "==", hymnTitle))
    );

    if (existingHymnSnapshot.empty) {
      setSelectedTitle("");
      setSelectedScale("");
      setSelectedNumber("");
      setSelectedCategory("");
      setEditData(false);
      return;
    }
    const hymnDoc = existingHymnSnapshot.docs[0];
    const hymnDocRef = doc(db, "hymns", hymnDoc.id);

    const hymnData = hymnDoc.data();
    const fileId = hymnData.fileId;
    const notesFileId = hymnData.notesFileId;

    if (fileId !== "") {
      const chordsstorageRef = ref(storage, `chords/${fileId}`);
      await deleteObject(chordsstorageRef);
    }

    if (notesFileId !== "") {
      const notesstorageRef = ref(storage, `notes/${notesFileId}`);
      await deleteObject(notesstorageRef);
    }

    // Delete the document
    await deleteDoc(hymnDocRef);

    setSelectedTitle("");
    setSelectedScale("");
    setSelectedNumber("");
    setSelectedCategory("");
    setEditData(false);
    setEditDetailsMode(false);
    handleCloseDeleteConfirmation();
    navigate("/");
  };

  const handleSaveEditData = async () => {
    // Check if title already exists
    const existingHymnSnapshot = await getDocs(
      query(collection(db, "hymns"), where("title", "==", hymnTitle))
    );
    if (hymnTitle !== selectedTitle) {
      const otherHymnSnapshot = await getDocs(
        query(collection(db, "hymns"), where("title", "==", selectedTitle))
      );
      if (!otherHymnSnapshot.empty) {
        const otherHymnDoc = otherHymnSnapshot.docs[0];
        const otherData = otherHymnDoc.data();
        if (otherData.title === selectedTitle && selectedTitle !== hymnTitle) {
          showToastMessage("Ο τίτλος υπάρχει ήδη");
          return;
        }
      }
    }

    if (existingHymnSnapshot.empty) {
      setSelectedTitle("");
      setSelectedScale("");
      setSelectedNumber("");
      setSelectedCategory("");
      setEditData(false);
      return;
    }
    const hymnDoc = existingHymnSnapshot.docs[0];
    const hymnDocRef = doc(db, "hymns", hymnDoc.id);

    const data = hymnDoc.data();
    data.title = selectedTitle !== "" ? selectedTitle : data.title;
    data.scale = selectedScale !== "" ? selectedScale : data.scale;
    data.number = selectedNumber !== "" ? selectedNumber : data.number;
    data.category = selectedCategory !== "" ? selectedCategory : data.category;
    data.visible = isHymnPublic;

    // Convert Greek to English
    const cleanTitle = transliterateGreekToEnglish(data.title);

    if (selectedFile) {
      try {
        let fileToUpload;
        if (previewBodyHtml && previewBodyHtml.trim()) {
          // Preview has parsed (and possibly been edited). Use it.
          const fullHtml = buildHymnHtml(previewBodyHtml);
          fileToUpload = new Blob([fullHtml], { type: "text/html" });
        } else {
          // Fallback: raw clean pipeline (parse may have failed earlier)
          let processedFile = selectedFile;
          const isDocx =
            selectedFile.name.endsWith(".docx") ||
            selectedFile.type ===
              "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
          if (isDocx) {
            processedFile = await convertDocxToHtml(selectedFile);
          }
          fileToUpload = cleanHtml
            ? await cleanHtmlFile(processedFile)
            : processedFile;
        }

        const fileId = `${cleanTitle}_chords_${uuidv4()}.html`;
        const chordsstorageRef = ref(storage, `chords/${fileId}`);
        // Upload the file data
        await uploadBytes(chordsstorageRef, fileToUpload);

        //delete old file if not empty
        if (data.fileId !== "") {
          const oldchordsStorageRef = ref(storage, `chords/${data.fileId}`);
          await deleteObject(oldchordsStorageRef);
        }

        data.fileId = fileId;
      } catch (error) {
        console.error("Error processing file:", error);
        showToastMessage(
          error.message || "Σφάλμα κατά την επεξεργασία του αρχείου"
        );
        return;
      }
    }

    if (selectedNotesFile) {
      try {
        const notesFileId = `${cleanTitle}_notes_${uuidv4()}.pdf`;

        const fileToUpload = stripPdfHeaderFooter
          ? await removeHeaderFooterFromPdf(selectedNotesFile)
          : selectedNotesFile;

        const storagenotesNotesRef = ref(storage, `notes/${notesFileId}`);
        await uploadBytes(storagenotesNotesRef, fileToUpload);

        //delete old file if not empty
        if (data.notesFileId !== "") {
          const storagenotesRefDel = ref(storage, `notes/${data.notesFileId}`);
          await deleteObject(storagenotesRefDel);
        }

        data.notesFileId = notesFileId;
      } catch (err) {
        showToastMessage("Σφάλμα κατά το ανέβασμα του PDF");
        return;
      }
    }

    //update the document
    await updateDoc(hymnDocRef, data);

    setSelectedTitle("");
    setSelectedScale("");
    setSelectedNumber("");
    setSelectedCategory("");
    setEditData(false);
    const currentUrl = new URL(window.location.href);
    currentUrl.searchParams.set("title", data.title);

    window.location.href = currentUrl.toString();
  };

  const handleTitleChange = (e) => {
    const title = e.target.value;

    if (!title) {
      setSelectedTitle(title);
      return;
    }

    // More permissive validation - allow Greek characters (including accents), spaces, common punctuation, and numbers
    // Allow parentheses, apostrophes (all types), commas, periods, dashes, and other common punctuation
    // \u0027: straight apostrophe '
    // \u2018: left single quotation mark '
    // \u2019: right single quotation mark '
    const constraints = {
      title: {
        format: /^[\u0370-\u03FF\u1F00-\u1FFF\s\-,.()\u0027\u2018\u20190-9]+$/,
      },
    };
    const validation = validate({ title }, constraints);

    if (!validation) {
      setSelectedTitle(title);
    } else {
      showToastMessage(
        "Ο τίτλος μπορεί να περιέχει μόνο ελληνικούς χαρακτήρες, αριθμούς, κενά και κοινά σημεία στίξης (.,'-())"
      );
    }
  };

  const handleNumberChange = (e) => {
    setSelectedNumber(e.target.value);
  };

  const handleCategoryChange = (e) => {
    setSelectedCategory(e.target.value);
  };

  const handleScaleChange = (e) => {
    setSelectedScale(e.target.value);
    let selected = majorScales
      .concat(minorScales)
      .find((option) => option.value === e.target.value);
    if (selected) setSelectedImage(selected.image);
  };

  const handleVisibilityChange = (e) => {
    setIsHymnPublic(e.target.checked);
  };

  const removeHeaderFooterFromPdf = async (
    file,
    headerHeight = 50,
    footerHeight = 50
  ) => {
    const buffer = await file.arrayBuffer();
    const pdfDoc = await PDFDocument.load(buffer);
    const pages = pdfDoc.getPages();

    pages.forEach((page) => {
      const { width, height } = page.getSize();

      // Draw white rectangle over header
      page.drawRectangle({
        x: 0,
        y: height - headerHeight,
        width: width,
        height: headerHeight,
        color: rgb(1, 1, 1),
      });

      // Draw white rectangle over footer
      page.drawRectangle({
        x: 0,
        y: 0,
        width: width,
        height: footerHeight,
        color: rgb(1, 1, 1),
      });
    });

    const pdfBytes = await pdfDoc.save();
    return new File([pdfBytes], file.name, { type: "application/pdf" });
  };

  return (
    <>
      <Modal show={editData} onHide={handleCloseEditData}>
        <Modal.Header closeButton>
          <Modal.Title>Επεξεργασία Στοιχείων Ύμνου</Modal.Title>
        </Modal.Header>
        <Modal.Body className="d-flex flex-column justify-content-center">
          <div className=" pt-2 mx-auto">
            <Form.Label>Τίτλος</Form.Label>
            <Form.Control
              type="text"
              value={selectedTitle}
              onChange={handleTitleChange}
              placeholder="Όνομα τίτλου"
            />
          </div>
          <div className="mx-auto pt-4">
            <Form.Label>Αρχείο (στίχοι με συγχορδίες) - HTML ή DOCX</Form.Label>
            <Form.Control
              type="file"
              accept=".html,.docx,text/html,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              ref={fileInputRef}
              onChange={handleFileChange}
            />
            <Form.Check
              type="checkbox"
              label="Καθαρισμός HTML"
              checked={cleanHtml}
              onChange={(e) => setCleanHtml(e.target.checked)}
              className="pt-2"
            />
          </div>
          {previewBodyHtml && (
            <div className="mx-auto pt-3">
              <Form.Label>Προεπισκόπηση</Form.Label>
              <HymnEditor
                initialHtml={previewBodyHtml}
                onChange={setPreviewBodyHtml}
              />
            </div>
          )}
          <div className="mx-auto pt-4">
            <Form.Label>Αρχείο με νότες (προαιρετικά)</Form.Label>
            <Form.Control
              type="file"
              ref={fileNotesInputRef}
              onChange={handleNotesFileChange}
            />
            <Form.Check
              type="checkbox"
              label="Strip pdf"
              checked={stripPdfHeaderFooter}
              onChange={(e) => setStripPdfHeaderFooter(e.target.checked)}
              className="pt-2"
            />
          </div>
          {/* <div className="mx-auto pt-4">
            <Form.Label>Αριθμός Ύμνου (προαιρετικά)</Form.Label>
            <Form.Control
              type="number"
              min="1"
              value={selectedNumber}
              onChange={handleNumberChange}
              placeholder="Αριθμός"
            />
          </div> */}
          <div className="w-60 pt-4 pb-4 mx-auto">
            <Form.Label>Τονικότητα</Form.Label>
            <Form.Select
              aria-label="Default select example"
              value={selectedScale}
              onChange={handleScaleChange}
            >
              <option key="0" value="0" label="Τονικότητα"></option>
              {majorScales.concat(minorScales).map((option) => (
                <option
                  key={option.value}
                  value={option.value}
                  style={{ backgroundImage: `url(${option.image})` }}
                >
                  {option.label}
                </option>
              ))}
            </Form.Select>
            <img className="pt-4 mx-auto" src={selectedImage} alt="" />
          </div>
          <div className="w-60 pt-4 pb-4 mx-auto">
            <Form.Label>Κατηγορία</Form.Label>
            <Form.Select
              aria-label="Default select example"
              value={selectedCategory}
              onChange={handleCategoryChange}
            >
              <option key="0" value="0" label="Κατηγορία"></option>
              {hymnCategories.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </Form.Select>
          </div>
          {/* <div className="pt-4 mx-auto">
            <Form.Label>Δημοσίευση ύμνου</Form.Label>
            <Switch
              checked={isHymnPublic}
              onChange={handleVisibilityChange}
              name="visibilityToggle"
              inputProps={{ "aria-label": "secondary checkbox" }}
            />
          </div> */}
          {showToastService && <ToastService message={toastMessage} />} {}
        </Modal.Body>
        <Modal.Footer className="d-flex justify-content-center align-items-center">
          <Button variant="secondary" onClick={handleCloseEditData}>
            Κλείσιμο
          </Button>
          <Button variant="danger" onClick={handleShowDeleteConfirmation}>
            Διαγραφή Ύμνου
          </Button>
          <Button variant="primary" onClick={handleSaveEditData}>
            Αποθήκευση
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showDeleteConfirmation}
        onHide={handleCloseDeleteConfirmation}
      >
        <Modal.Header closeButton>
          <Modal.Title>Επιβεβαίωση Διαγραφής</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Είστε σίγουροι ότι θέλετε να διαγράψετε τον ύμνο;
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseDeleteConfirmation}>
            Ακύρωση
          </Button>
          <Button variant="danger" onClick={handleDeleteHymn}>
            Διαγραφή
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};
